import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { TimeSeriesAreaChartRecharts } from '../../../../shared/charts/recharts';
import {
  buildValueLabelRow,
  formatTemperatureChartLabel,
  getClosestEnrichedPoint,
  type EnrichedGraphSeriesPoint,
} from '../../helpers/graph/graphData';
import {
  buildTemperatureFillGradientStops,
  buildTemperatureLineGradientStops,
  getTemperatureChartColor,
  getTemperatureChartLineColor,
  getTemperatureHeatYDomain,
} from '../../helpers/graph/temperatureHeatGradient';
import {
  WEATHER_BEZIER_AREA_CURVE,
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';

export interface TemperatureChartProps extends WeatherChartBaseProps {
  temperatureSeries: EnrichedGraphSeriesPoint[];
}

const TemperatureChart: FC<TemperatureChartProps> = ({
  data,
  domain,
  timeZone,
  temperatureSeries,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const labelRows = useMemo(
    () => [
      buildValueLabelRow(data, (value) =>
        formatTemperatureChartLabel(value, unitsConfig),
      ),
    ],
    [data, unitsConfig],
  );

  const yDomain = useMemo(
    () => getTemperatureHeatYDomain(data.map((point) => point.value)),
    [data],
  );

  const fillGradientStops = useMemo(
    () => buildTemperatureFillGradientStops(),
    [],
  );

  const lineGradientStops = useMemo(
    () => buildTemperatureLineGradientStops(),
    [],
  );

  const scrubberDotColor = useMemo(() => {
    const selectedPoint = selectedTime
      ? getClosestEnrichedPoint(temperatureSeries, selectedTime)
      : defaultSelectedTime
        ? getClosestEnrichedPoint(temperatureSeries, defaultSelectedTime)
        : undefined;

    if (selectedPoint?.value != null) {
      return getTemperatureChartColor(
        selectedPoint.value,
        yDomain[0],
        yDomain[1],
      );
    }

    return getTemperatureChartColor(yDomain[1], yDomain[0], yDomain[1]);
  }, [defaultSelectedTime, selectedTime, temperatureSeries, yDomain]);

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  const [yMin, yMax] = yDomain;

  return (
    <TimeSeriesAreaChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      curve={WEATHER_BEZIER_AREA_CURVE}
      colors={{
        line: getTemperatureChartLineColor(yMax, yMin, yMax),
        fill: {
          top: getTemperatureChartColor(yMax, yMin, yMax),
          bottom: getTemperatureChartColor(yMin, yMin, yMax),
        },
        scrubberDot: scrubberDotColor,
      }}
      fillGradientStops={fillGradientStops}
      lineGradientStops={lineGradientStops}
      yDomain={yDomain}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default TemperatureChart;
